import { useEffect, useMemo, useState } from "react";

import {
  AnchoredPopup,
  AnchoredPopupBody,
  AnchoredPopupFooter,
  useAnchoredPopupAnchor,
} from "../../AnchoredPopup";
import { Field } from "../../Field";
import { IconButton } from "../../IconButton";
import { CalendarIcon } from "../../../icons/glyphs/CalendarIcon";
import { Calendar } from "./Calendar";
import { formatDate, getLocaleWeekStart, startOfDay } from "./dateUtils";
import { PickerRoot, PickerTrigger } from "../pickerTrigger";
import { useTriggerToggle } from "../../foundations/popoverTrigger";
import { useDateRangeSelection } from "./useDateRangeSelection";
import type { DateRangePickerProps, DateRangeValue } from "./types";

/**
 * 날짜 범위 선택 컨트롤(RFC-date-picker). Field로 감싼 트리거 → AnchoredPopup에 캘린더를 띄워
 * 시작·종료 두 날짜를 고른다. v1: 데스크톱 docked · range · 캘린더 전용 · zero-dep.
 * `confirm`이면 즉시 적용 대신 Cancel/OK 푸터로 확정(draft 스테이징).
 * popover(§2)·surface(§3)·focus/transition(§5-C)는 전부 기존 c6 파운데이션 재사용.
 */
export const DateRangePicker = ({
  value,
  onChange,
  min,
  max,
  isDateDisabled,
  locale,
  weekStartsOn,
  disabled = false,
  label,
  size = "md",
  placement = "bottom-start",
  id,
  className,
  confirm = false,
  confirmLabel = "OK",
  cancelLabel = "Cancel",
  placeholder = "Select date range",
  renderTrigger,
}: DateRangePickerProps) => {
  const popup = useAnchoredPopupAnchor();
  const resolvedWeekStart = weekStartsOn ?? getLocaleWeekStart(locale);

  // confirm 모드에서는 draft로 스테이징, 아니면 value에 즉시 반영.
  const [draft, setDraft] = useState<DateRangeValue>(value);
  const active = confirm ? draft : value;
  const commitTarget = confirm ? setDraft : onChange;
  const selection = useDateRangeSelection({ value: active, onChange: commitTarget });

  const [viewDate, setViewDate] = useState<Date>(() =>
    startOfDay(value.start ?? value.end ?? new Date()),
  );

  // 팝업 열릴 때 draft를 현재 value로 리셋하고 표시 달을 맞춘다.
  useEffect(() => {
    if (popup.open) {
      if (confirm) setDraft(value);
      setViewDate(startOfDay(value.start ?? value.end ?? new Date()));
    }
    // 열림 전이에서만 seeding — value 변화로 매번 리셋하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup.open]);

  const triggerLabel = useMemo(() => {
    if (value.start && value.end) {
      return `${formatDate(value.start, locale)} – ${formatDate(value.end, locale)}`;
    }
    if (value.start) {
      return `${formatDate(value.start, locale)} – …`;
    }
    return null; // 미선택 → placeholder 렌더.
  }, [value.start, value.end, locale]);

  const handleSelectDate = (date: Date) => {
    // confirm 모드: draft만 갱신하고 팝업 유지(OK로 확정).
    if (confirm) {
      selection.selectDate(date);
      return;
    }
    // 즉시 모드: 이번 클릭으로 range가 완성되면(시작만 있던 상태) 선택 후 닫는다.
    // 같은 날을 다시 클릭한 경우도 완성(1일 range)으로 보고 닫는다(bug: 이전엔 같은 날 제외로 안 닫힘).
    const willComplete = value.start != null && value.end == null;
    selection.selectDate(date);
    if (willComplete) popup.close();
  };

  const trigger = useTriggerToggle(popup, disabled);

  return (
    <PickerRoot className={className}>
      {renderTrigger ? (
        // 커스텀 트리거: 소비자 요소로 열고 값은 onChange로 표시. 기본 Field/트리거는 렌더 안 함.
        renderTrigger({ ...trigger, isOpen: popup.open, value, disabled })
      ) : (
        <Field label={label} layout="stack" disabled={disabled} id={id}>
          <PickerTrigger
            disabled={disabled}
            placeholder={value.start == null}
            size={size}
            {...trigger.triggerProps}
          >
            <span>{triggerLabel ?? placeholder}</span>
            <CalendarIcon aria-hidden="true" />
          </PickerTrigger>
        </Field>
      )}

      <AnchoredPopup
        open={popup.open}
        anchor={popup.anchor}
        onClose={popup.close}
        placement={placement}
        role="dialog"
        aria-label={typeof label === "string" ? label : "Choose date range"}
        // 두 달 나란히(dual-pane) 폭 수용 — 기본 max-width(28rem)면 오른쪽 달이 잘린다.
        // pane 2×(CELL*7=15.75rem) + gap + body padding ≈ 34.5rem.
        maxWidth="min(37rem, calc(100vw - 1rem))"
      >
        <AnchoredPopupBody padding="md">
          <Calendar
            viewDate={viewDate}
            onViewDateChange={setViewDate}
            weekStartsOn={resolvedWeekStart}
            locale={locale}
            min={min}
            max={max}
            isDateDisabled={isDateDisabled}
            selection={selection}
            onSelectDate={handleSelectDate}
            initialFocusDate={active.start ?? active.end ?? new Date()}
            months={2}
            aria-label="Date range"
          />
        </AnchoredPopupBody>
        {confirm ? (
          <AnchoredPopupFooter>
            <IconButton variant="ghost" text={cancelLabel} onClick={popup.close} />
            <IconButton
              variant="solid"
              tone="primary"
              text={confirmLabel}
              // range는 양 끝이 모두 있어야 확정 가능.
              disabled={draft.start == null || draft.end == null}
              onClick={() => {
                onChange(draft);
                popup.close();
              }}
            />
          </AnchoredPopupFooter>
        ) : null}
      </AnchoredPopup>
    </PickerRoot>
  );
};
